import React, { Component } from 'react';
import cx from 'classnames';
import _ from 'lodash';
import Color from 'color';
import css from 'styled-jsx/css';
import AutosizeInput from 'react-input-autosize';

import SearchIcon from './SearchIcon';

interface IInputTextProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  fill?: string;
  shouldFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  customProps?: React.InputHTMLAttributes<HTMLInputElement>;
  autosize?: boolean;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactElement;
  typeCustom?: string;
  wide?: boolean;
  full?: boolean;
  minlength?: string;
  strong?: boolean;
  invalid?: boolean;
  colors?: {
    input: string;
    icon: string;
    text: string;
    placeholder: string;
    focusBackgroundColor: string;
  };
  containerStyle?: React.CSSProperties;
  value?: $FIXME;
}

/** @deprecated - prefer `design-system/TextField` */
export default class InputText extends Component<IInputTextProps> {
  private input: HTMLInputElement | null = null;

  static defaultProps = {
    size: 'md',
    type: 'text',
    colors: {
      input: '#f5f4f9',
      icon: '#636E7D',
      text: Color('#636E7D').darken(0.3),
      placeholder: '#757575',
      focusBackgroundColor: 'rgb(237,235,244)',
    },
  };

  componentDidMount() {
    if (this.props.shouldFocus && !this.props.disabled && this.input !== null) {
      this.input.focus();
    }
  }

  render() {
    const {
      placeholder,
      type,
      typeCustom,
      value,
      wide,
      disabled,
      size,
      icon,
      full,
      strong,
      invalid,
      colors,
      shouldFocus,
      customProps,
      containerStyle,
      autosize,
      inputRef,
      ...props
    } = this.props;

    const { className: jsxClassName, styles } = jsxStyles(colors);

    const className = cx(jsxClassName, 'input', {
      'input--wide': wide,
      'input--icon': !!icon || typeCustom === 'search',
      'input--disabled': disabled,
      'input--sm': size === 'sm',
      'input--lg': size === 'lg',
      'input--full': full,
      'input--strong': strong,
      'input--invalid': invalid,
    });

    const ref = (node: HTMLInputElement | null) => {
      this.input = node;
      if (typeof this.props.inputRef === 'function') {
        this.props.inputRef(node);
      }
    };

    return (
      <div className={jsxClassName + ' container'} style={containerStyle}>
        {typeCustom === 'search' && <SearchIcon fill={colors!.icon} />}
        {typeCustom !== 'search' && icon && (
          <div className={jsxClassName + ' input-icon'}>{icon}</div>
        )}

        {autosize ? (
          <AutosizeInput
            {...((customProps as $FIXME) || {})}
            {...props}
            type={type}
            inputClassName={className + ' input--autosize'}
            injectStyles={false}
            minWidth={20}
            inputRef={ref}
            value={value}
            placeholder={placeholder}
          />
        ) : (
          <input
            {...(customProps || {})}
            {...props}
            type={type}
            className={className}
            placeholder={placeholder}
            value={value}
            ref={ref}
          />
        )}

        {styles}
        <style jsx global>{`
          .input-icon .fa {
            color: var(--color-form-input) !important;
            font-size: 13px;
            position: absolute;
            top: 3px;
            left: 2px;
          }
          .input-icon svg {
            height: 100%;
            width: 100%;
          }
          .input-icon svg,
          .input-icon svg path {
            fill: var(--text-color);
          }
        `}</style>
      </div>
    );
  }
}

const jsxStyles = _.memoize(
  colors => css.resolve`
    .container {
      position: relative;
    }

    .input-icon {
      position: absolute;
      left: 10px;
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      top: calc(50% - 8px);
    }

    .input {
      background-color: ${colors.input};
      color: ${colors.text};
      padding: 7px 10px 7px 14px;
      border-radius: 8px;
      width: calc(100% - 20px);
      font-size: 14px;
      font-family: var(--font-family);
      border: 0;
      box-sizing: border-box;
      line-height: 22px;
      transition: 0.3s ease background-color, 0.05s ease width;
    }

    .input:focus {
      background-color: ${colors.focusBackgroundColor};
    }

    .input ::placeholder {
      font-weight: normal;
    }

    :global(.input-prepend) + * input {
      border-radius: 0 4px 4px 0;
      margin-left: 0;
      width: 100%;
      flex: 1;
    }

    .input--wide {
      width: 220px;
    }

    .input--invalid {
      border: 1px solid red;
    }

    .input--icon {
      padding-left: 32px;
      padding-right: 10px;
    }

    .input--disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    .input--sm {
      padding: 2px 8px;
      width: auto;
    }

    .input--lg {
      font-size: 24px;
      min-width: 400px;
    }

    .input--full {
      width: 100%;
    }

    .input--strong {
      font-weight: bold;
    }

    .input--autosize {
      width: auto;
    }

    svg {
      margin-top: -1px;
    }

    input:disabled {
      color: #999;
    }

    ::-webkit-input-placeholder {
      color: ${colors.placeholder};
    }
    ::-moz-placeholder {
      color: ${colors.placeholder};
    }
    :-ms-input-placeholder {
      color: ${colors.placeholder};
    }
    :-moz-placeholder {
      color: ${colors.placeholder};
    }
  `,
);
